import { TFunction } from 'i18next';

import {
  ErpGroupDiplomasQuery,
  PreviewDiplomaMatchMutation,
  DiplomaLanguageEnum,
  Maybe,
} from 'core/graphql';

/**
 * Spec 0016 — shared diploma domain model: wire types re-exported from the
 * generated hooks, the plain-String match `status` vocabulary, and the
 * customErrors → localized message mapping. Mirrors `Surveys/surveyModel.ts`.
 */

/* ---------- wire types ---------- */

export type IGroupDiplomaRow = ErpGroupDiplomasQuery['groupDiplomas'][number];
export type IDiplomaSlot = NonNullable<IGroupDiplomaRow['pl']>;
export type IDiplomaStudent = IGroupDiplomaRow['student'];

export type IDiplomaMatchPreview = NonNullable<
  NonNullable<PreviewDiplomaMatchMutation['matchDiplomaFiles']>['preview']
>;
export type IDiplomaMatchRow = IDiplomaMatchPreview['rows'][number];
export type IDiplomaMatchSummary = IDiplomaMatchPreview['summary'];
export type IDiplomaUnmatchedStudent = IDiplomaMatchPreview['unmatchedStudents'][number];

/** The two diploma slots every roster row carries. */
export const DIPLOMA_LANGUAGES: readonly DiplomaLanguageEnum[] = [
  DiplomaLanguageEnum.Pl,
  DiplomaLanguageEnum.En,
];

/* ---------- picked-file identity (bulk attach) ---------- */

/**
 * One file the operator picked for the bulk attach, tagged with an id unique
 * WITHIN that batch.
 *
 * The file NAME is deliberately not that id. Two files picked from different
 * folders can both be called `diploma.pdf` — routine for scanned or exported
 * documents — and `matchDiplomaFiles` echoes back only the name. Keying preview
 * rows, operator decisions, upload outcomes or React children by the name then
 * collapses the pair: choosing a student for one row overwrites the other, only
 * one of the two `File` objects is ever uploaded, and BOTH rows report success —
 * the operator sees 100% while a student silently has no diploma. Everything
 * downstream joins on `uid` instead.
 */
export interface IPickedDiplomaFile {
  uid: string;
  file: File;
}

/** Tag a freshly picked batch with per-batch-unique ids. */
export const toPickedDiplomaFiles = (
  files: readonly File[],
): IPickedDiplomaFile[] =>
  files.map((file, index) => ({ uid: `${index}:${file.name}`, file }));

/** A preview row joined back to the `File` whose bytes it will upload. */
export interface ICorrelatedDiplomaRow<TRow> {
  uid: string;
  row: TRow;
  /**
   * Absent only if the backend returned more rows for a name than were picked —
   * such a row is skipped at commit rather than uploading someone else's bytes.
   */
  file?: File;
}

/**
 * Join the backend's preview rows onto the picked `File` objects.
 *
 * The backend's only join key is `fileName`, which is not unique across a batch.
 * It does preserve input order (`match_files` emits exactly one row per entry,
 * in order), so the Nth row named `X` is the Nth picked file named `X`. Pairing
 * on that ordinal — rather than on the name alone — keeps the mapping a
 * bijection even when names repeat, so no file can be silently dropped and no
 * two rows can claim the same bytes.
 */
export const correlateDiplomaRows = <TRow extends { fileName: string }>(
  rows: readonly TRow[],
  picked: readonly IPickedDiplomaFile[],
): Array<ICorrelatedDiplomaRow<TRow>> => {
  const byName = new Map<string, IPickedDiplomaFile[]>();
  picked.forEach((entry) => {
    const bucket = byName.get(entry.file.name);
    if (bucket) bucket.push(entry);
    else byName.set(entry.file.name, [entry]);
  });

  const used = new Map<string, number>();
  return rows.map((row, index) => {
    const ordinal = used.get(row.fileName) || 0;
    used.set(row.fileName, ordinal + 1);
    const entry = byName.get(row.fileName)?.[ordinal];
    return {
      // The fallback still has to be unique, or an unmatched row would collide
      // with its neighbours in `decisions` / `outcomes` all over again.
      uid: entry ? entry.uid : `unpicked-${index}:${row.fileName}`,
      row,
      file: entry?.file,
    };
  });
};

/* ---------- match row status (plain String on the wire) ---------- */

/**
 * Per-file outcome of the `matchDiplomaFiles` dry-run.
 *   - `MATCHED`            — a single confident student match.
 *   - `AMBIGUOUS`          — several candidates; the operator must choose.
 *   - `NO_MATCH`           — no candidate found.
 *   - `DUPLICATE_IN_BATCH` — two picked files resolve to the same slot.
 *   - `NOT_PDF`            — the file is not a PDF.
 */
export const DIPLOMA_MATCH_STATUSES = [
  'MATCHED',
  'AMBIGUOUS',
  'NO_MATCH',
  'DUPLICATE_IN_BATCH',
  'NOT_PDF',
] as const;

export type DiplomaMatchStatus = typeof DIPLOMA_MATCH_STATUSES[number];

/**
 * Only a row that resolves to exactly one student AND one language can be
 * uploaded. `NOT_PDF` is hard-blocked because it is a fact about the file's
 * bytes that no amount of re-targeting can change, so a non-PDF must never
 * reach the upload mutation.
 *
 * `DUPLICATE_IN_BATCH` is deliberately NOT checked here. That status is the
 * server's verdict on the ORIGINAL guesses and is never recomputed, so
 * blocking on it left a re-targeted row permanently unuploadable: the operator
 * would fix the collision, watch the warning disappear, and still find both
 * rows skipped and «Загрузить» disabled with nothing left to correct.
 * Collisions are owned by the caller's live `duplicateUids` set, which is
 * recomputed from the current decisions on every edit.
 */
export const isUploadableMatchRow = (
  studentId: string | undefined,
  language: DiplomaLanguageEnum | null | undefined,
  status: string,
): boolean => Boolean(studentId) && Boolean(language) && status !== 'NOT_PDF';

/* ---------- customErrors → readable message ---------- */

/** The backend diploma error codes the UI translates explicitly. */
export const DIPLOMA_ERROR_CODES = [
  'ALREADY_EXISTS',
  'NOT_PDF',
  'FILE_TOO_LARGE',
  'STUDENT_NOT_IN_GROUP',
  'NOT_FOUND',
  'PERMISSION_DENIED',
] as const;

interface IDiplomaCustomError {
  code?: Maybe<string>;
  message?: Maybe<string>;
}

/**
 * Map the first customError onto a localized message: a known code resolves
 * through `pages.diplomas.errors.<CODE>`; anything else falls back to the raw
 * backend message (or a generic error).
 */
export const diplomaErrorMessage = (
  t: TFunction,
  customErrors?: Maybe<Array<Maybe<IDiplomaCustomError>>>,
): string => {
  const first = (customErrors || []).find(Boolean);
  const code = first?.code || '';
  if ((DIPLOMA_ERROR_CODES as readonly string[]).includes(code)) {
    return t(`pages.diplomas.errors.${code}`);
  }
  return first?.message || t('pages.diplomas.errors.generic');
};

/** Human-readable file size for the slot tooltip («1,4 МБ»). */
export const formatDiplomaSize = (sizeBytes?: Maybe<number>): string => {
  if (!sizeBytes || sizeBytes <= 0) return '';
  const mb = sizeBytes / (1024 * 1024);
  if (mb >= 1) return `${mb.toFixed(1)} MB`;
  return `${Math.max(1, Math.round(sizeBytes / 1024))} KB`;
};
